import { Leaf } from 'lucide-react';
import { apiPaths, type NowResponse } from '../../../shared/api/contract.ts';
import { DEFAULT_CITY } from '../../../shared/cities/index.ts';
import {
  AIR_QUALITY_LEVELS,
  POLLUTANT_LABELS,
  type AirQualityLevel,
  type AirQualityObservation,
} from '../../../shared/domain/index.ts';
import { PageContainer } from '../../components/layout/PageContainer.tsx';
import { SectionCard } from '../../components/SectionCard.tsx';
import { useApi } from '../../hooks/useApi.ts';
import { formatDay } from '../../lib/format.ts';

const LEVELS: readonly AirQualityLevel[] = [1, 2, 3, 4, 5, 6];

function Today({ air }: { air: AirQualityObservation }) {
  const { label, color } = AIR_QUALITY_LEVELS[air.level];
  const responsible = air.responsiblePollutants.map((pollutant) => POLLUTANT_LABELS[pollutant]);
  return (
    <>
      <p className="lead">
        <span className="status-dot" style={{ background: color }} aria-hidden="true" />
        <strong>{label}</strong> · indice ATMO {air.isForecast ? 'prévu pour le' : 'du'}{' '}
        <time dateTime={air.validOn}>{formatDay(air.validOn)}</time>
      </p>
      {responsible.length > 0 && (
        <p>
          Polluant{responsible.length > 1 ? 's' : ''} qui détermine{responsible.length > 1 ? 'nt' : ''} l’indice
          aujourd’hui : {responsible.join(', ')}.
        </p>
      )}
      {air.pollutants.length > 0 && (
        <table className="data-table">
          <thead>
            <tr>
              <th scope="col">Polluant</th>
              <th scope="col">Sous-indice</th>
              <th scope="col">Concentration</th>
            </tr>
          </thead>
          <tbody>
            {air.pollutants.map((reading) => (
              <tr key={reading.pollutant}>
                <th scope="row">{POLLUTANT_LABELS[reading.pollutant]}</th>
                <td>{reading.level === undefined ? 'non publié' : AIR_QUALITY_LEVELS[reading.level].label}</td>
                <td>
                  {reading.concentrationUgM3 === undefined
                    ? 'non publiée'
                    : `${Math.round(reading.concentrationUgM3)} µg/m³`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

/** Page dédiée à la qualité de l'air : l'indice du jour, et comment il est construit. */
export function AirPage() {
  const now = useApi<NowResponse>(apiPaths.now(DEFAULT_CITY.slug));
  return (
    <PageContainer path="/air">
      <SectionCard
        title="Indice ATMO à Ermont"
        icon={<Leaf size={20} aria-hidden="true" />}
        kind="airQuality"
        section={now.data?.airQuality}
        loading={now.loading}
        fetchError={now.error}
        subject="L’indice de qualité de l’air"
      >
        {(data) => <Today air={data} />}
      </SectionCard>

      <section className="card" aria-labelledby="air-how">
        <h2 id="air-how">Comment l’indice est construit</h2>
        <p>
          L’indice ATMO est défini par l’arrêté du 10 juillet 2020. Il est calculé chaque jour à partir de cinq
          polluants réglementés, et retient <strong>le plus défavorable des cinq</strong> : une seule valeur élevée
          suffit donc à dégrader l’indice de la journée.
        </p>
        <ul>
          {Object.values(POLLUTANT_LABELS).map((label) => (
            <li key={label}>{label}</li>
          ))}
        </ul>
        <p>
          C’est un indice <strong>journalier et communal</strong>, issu d’un modèle qui combine mesures et prévisions.
          Il décrit l’air de la commune dans son ensemble, pas celui d’une rue en particulier : le long d’un axe très
          circulé, l’air respiré peut être plus dégradé que ne le dit l’indice.
        </p>
      </section>

      <section className="card" aria-labelledby="air-scale">
        <h2 id="air-scale">L’échelle en six niveaux</h2>
        <ul className="item-list">
          {LEVELS.map((level) => (
            <li key={level}>
              <span className="status-dot" style={{ background: AIR_QUALITY_LEVELS[level].color }} aria-hidden="true" />
              <strong>
                {level}. {AIR_QUALITY_LEVELS[level].label}
              </strong>
            </li>
          ))}
        </ul>
        <p className="muted">
          Ermont Live affiche l’indice et les concentrations tels qu’ils sont publiés, sans y ajouter de recommandation
          sanitaire. Les consignes en cas d’épisode de pollution sont publiées par l’association agréée de surveillance
          de la qualité de l’air et par la préfecture : ce sont elles qui font foi.
        </p>
      </section>
    </PageContainer>
  );
}
